import { createAction } from '@ngrx/store'
import { ACCEPT_VIN_8_FORM, AUTH_RESET, CLEAR_ODOMETER_LOOKUP, SET_ODOMETER_FLOOR, SET_ODOMETER_MIN } from '../../auth/auth.actions'
import { SET_DISTANCE_UNIT } from '../ui.actions'
import { CANCEL_EDITING_REQUEST } from '../../../shared/actions/shared.actions'
import { SET_ADDITIONAL_LOCATION_INFO, SET_PLATE_TO_VIN } from './forms.actions'
import { PayloadedAction } from '../../../shared/types';
import { DISTANCE_UNIT } from '../../../shared/i18n/i18n.types'
import { MEMBERSHIP_NUMBER_LENGTH } from './forms.constants'

/**
 * Forms are Angular `FormGroup`s owned by their components (see
 * `forms.factories.ts`). This slice keeps only what lives outside the owning
 * component: the two values other modules read, and the runtime config that
 * decides which validators those components apply.
 */

export interface AuthFormsConfigState {
  vinMileageAuthConfig: VinMileageAuthConfig
  odometerConfirmationConfig: OdometerConfirmationConfig
  odometerLookupConfig: OdometerLookupConfig
}
export interface CallRequestFormsState {
  additionalLocationInfo: AdditionalLocationInfoFormState
}
export interface PlateToVinFormsState {
  plateToVin: PlateToVinFormState
}
export type FormsState = AuthFormsConfigState &
  CallRequestFormsState &
  PlateToVinFormsState

export const RESET_PLATE_TO_VIN_FORM = 'RESET_PLATE_TO_VIN_FORM'

export { MEMBERSHIP_NUMBER_LENGTH }

export const setResetPlateToVinForm = createAction(RESET_PLATE_TO_VIN_FORM)

export interface MembershipNumberAuthFormState {
  membershipNumber: string
  zipCode: string
}

export interface NameAuthFormState {
  firstName: string
  lastName: string
  zipCode: string
}

export interface PhoneNumberFormState {
  phoneNumber: string
}

export interface CancelFeedbackOthersFormState {
  reason: string
}

export interface VinMileageAuthFormState {
  vinNumber: string
  mileage: number
}

export interface PlateToVinAuthFormState {
  licensePlate: string
  state: string
  mileage: number
}

export interface OdometerConfirmationFormState {
  mileage: number
}

export interface CarRentalAuthFormState {
  refNumber: number
}

export interface SubscriberNumberAuthFormState {
  subscriber: number
}

export interface AdditionalLocationInfoFormState {
  breakdownComment: string
}

export interface PlateToVinFormState {
  licensePlate: string
  state: string
  color: string
}

export interface VinMileageAuthConfig {
  acceptVin8Char: boolean
}

export interface OdometerConfirmationConfig {
  minOdometer: number
}

export interface OdometerLookupConfig {
  odometerFloor: number | null
  distanceUnit: DISTANCE_UNIT | null
}

export function createAuthFormConfigInitialState(): AuthFormsConfigState {
  return {
    vinMileageAuthConfig: {
      acceptVin8Char: false,
    },
    odometerConfirmationConfig: {
      minOdometer: 0,
    },
    odometerLookupConfig: {
      odometerFloor: null,
      distanceUnit: null,
    },
  }
}

export function createPlateToVinFormInitialState(): PlateToVinFormsState {
  return {
    plateToVin: {
      licensePlate: '',
      state: '',
      color: '',
    },
  }
}

export function createCallFormInitialState(): CallRequestFormsState {
  return {
    additionalLocationInfo: {
      breakdownComment: '',
    },
  }
}

export function createInitialState(): FormsState {
  return {
    ...createAuthFormConfigInitialState(),
    ...createCallFormInitialState(),
    ...createPlateToVinFormInitialState(),
  }
}

export function reducer(
  state: FormsState | undefined = createInitialState(),
  action: PayloadedAction
): FormsState {
  switch (action.type) {
    case AUTH_RESET:
      return {
        ...state,
        ...createAuthFormConfigInitialState(),
      }

    case CANCEL_EDITING_REQUEST:
      return {
        ...state,
        ...createCallFormInitialState(),
      }

    case ACCEPT_VIN_8_FORM:
      return {
        ...state,
        vinMileageAuthConfig: {
          ...state.vinMileageAuthConfig,
          acceptVin8Char: action.payload,
        },
      }

    case SET_ODOMETER_MIN:
      return {
        ...state,
        odometerConfirmationConfig: {
          minOdometer: action.payload ?? 0,
        },
      }

    case SET_DISTANCE_UNIT:
      return {
        ...state,
        odometerLookupConfig: {
          ...state.odometerLookupConfig,
          distanceUnit: action.payload,
        },
      }

    case SET_ODOMETER_FLOOR:
      // Only the floor is stored now. The components that own a mileage control
      // watch it, re-apply their validators and prefill the field, which the
      // reducer used to do by writing into the form state directly.
      return {
        ...state,
        odometerLookupConfig: {
          ...state.odometerLookupConfig,
          odometerFloor: action.payload.floor ?? null,
        },
      }

    case CLEAR_ODOMETER_LOOKUP:
      return {
        ...state,
        odometerLookupConfig: {
          ...state.odometerLookupConfig,
          odometerFloor: null,
        },
      }

    case SET_ADDITIONAL_LOCATION_INFO:
      return {
        ...state,
        additionalLocationInfo: action.payload,
      }

    case SET_PLATE_TO_VIN:
      return {
        ...state,
        plateToVin: action.payload,
      }

    case RESET_PLATE_TO_VIN_FORM:
      return {
        ...state,
        ...createPlateToVinFormInitialState(),
      }

    default:
      return state
  }
}
